import {
  Button,
  ButtonGroup,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tooltip,
} from "@chakra-ui/react";
import { ExportFinancialDocumentsButton } from "@components/modules/common/exports/financial-documents/ExportFinancialDocumentsButton";
import { TabTableNavigation } from "@components/modules/expenses/listing/Common";
import type { ExpenseListNavbarStore } from "@components/modules/expenses/listing/components/navbar/store";
import type { StoreExpenses } from "@components/modules/expenses/listing/StoreExpenses";
import {
  ButtonLink,
  ExportButton,
  InputDatePicker,
  ModalConfirm,
  Select,
} from "@components/ui-kit";
import { CancelButton } from "@components/ui-kit/Button/CancelButton";
import { Icon } from "@components/ui-kit/Icon";
import { Trans, t } from "@lingui/macro";
import { ExpensePaymentOrderExportType } from "@src/__generated__/graphql";
import { can } from "@src/utils/components/permissions";
import { toApiDate } from "@src/utils/dates";
import { fieldToSelectProps } from "@src/utils/forms/inputHelpers";
import { observer } from "mobx-react-lite";
import type { PropsWithChildren } from "react";

type ExpenseListNavbarProps = {
  navbarStore: ExpenseListNavbarStore;
  expensesStore: StoreExpenses;
};

function ExpenseListNavbar({
  expensesStore,
  navbarStore: store,
}: ExpenseListNavbarProps) {
  return (
    <HStack justifyContent="space-between">
      <TabTableNavigation store={expensesStore} />
      <HStack spacing="3">
        <Menu>
          <MenuButton
            as={ExportButton}
            isLoading={store.isExporting}
            rightIcon={<Icon name="chevron-down" />}
          />
          <MenuList>
            {store.showExpensesExport && (
              <MenuItem
                isDisabled={store.isExporting}
                onClick={store.exportExpenses}
              >
                <Trans>Expenses export</Trans>
              </MenuItem>
            )}

            {store.showExpensesSummaryExport && (
              <SelectExpensesFirstTooltip disabled={store.hasSelectedExpenses}>
                <MenuItem
                  isDisabled={!store.hasSelectedExpenses}
                  onClick={store.exportExpensesSummary}
                >
                  <Trans>Summary</Trans>
                </MenuItem>
              </SelectExpensesFirstTooltip>
            )}

            <SelectExpensesFirstTooltip disabled={store.hasSelectedExpenses}>
              <MenuItem
                isDisabled={!store.hasSelectedExpenses}
                onClick={store.paymentOrderModal.onOpen}
              >
                <Trans>Payment order</Trans>
              </MenuItem>
            </SelectExpensesFirstTooltip>

            <SelectExpensesFirstTooltip disabled={store.hasSelectedExpenses}>
              <MenuItem
                isDisabled={!store.hasSelectedExpenses}
                onClick={store.qrCodesModal.onOpen}
              >
                <Trans>QR codes</Trans>
              </MenuItem>
            </SelectExpensesFirstTooltip>

            <SelectExpensesFirstTooltip disabled={store.hasSelectedExpenses}>
              <MenuItem
                isDisabled={!store.hasSelectedExpenses}
                onClick={store.exportAbraFlexi}
              >
                <Trans>ABRA</Trans>
              </MenuItem>
            </SelectExpensesFirstTooltip>

            <MenuItem
              isDisabled={store.isExporting}
              onClick={store.exportExpensesXml}
            >
              <Trans>XML/XLS zip</Trans>
            </MenuItem>

            <ExportFinancialDocumentsButton />

            {store.showPremierExport && (
              <SelectExpensesFirstTooltip disabled={store.hasSelectedExpenses}>
                <MenuItem
                  isDisabled={!store.hasSelectedExpenses}
                  onClick={store.exportPremier}
                >
                  <Trans>Premier</Trans>
                </MenuItem>
              </SelectExpensesFirstTooltip>
            )}

            {store.showOmegaExport && (
              <SelectExpensesFirstTooltip disabled={store.hasSelectedExpenses}>
                <MenuItem
                  isDisabled={!store.hasSelectedExpenses}
                  onClick={store.exportOmega}
                >
                  <Trans>OMEGA</Trans>
                </MenuItem>
              </SelectExpensesFirstTooltip>
            )}

            {store.showPohodaExport && (
              <SelectExpensesFirstTooltip disabled={store.hasSelectedExpenses}>
                <MenuItem
                  isDisabled={!store.hasSelectedExpenses}
                  onClick={store.exportPohoda}
                >
                  <Trans>Pohoda</Trans>
                </MenuItem>
              </SelectExpensesFirstTooltip>
            )}

            {store.showVarioExport && (
              <SelectExpensesFirstTooltip disabled={store.hasSelectedExpenses}>
                <MenuItem
                  isDisabled={!store.hasSelectedExpenses}
                  onClick={store.exportVario}
                >
                  <Trans>Vario</Trans>
                </MenuItem>
              </SelectExpensesFirstTooltip>
            )}

            {store.showTransakcioExport && (
              <SelectExpensesFirstTooltip disabled={store.hasSelectedExpenses}>
                <MenuItem
                  isDisabled={!store.hasSelectedExpenses}
                  onClick={store.exportTransakcio}
                >
                  <Trans>Transakcio</Trans>
                </MenuItem>
              </SelectExpensesFirstTooltip>
            )}
          </MenuList>
        </Menu>

        <ModalConfirm
          {...store.qrCodesModalConfirm}
          message={t`Are you going to generate ${expensesStore.selectedExpensesIds.length} QR codes. Do you want to generate them?`}
          onConfirm={() =>
            store.exportPaymentOrder({
              payment_type_id: expensesStore.modalForms.qrCodes.$.card_id.$!,
              ids: expensesStore.selectedExpensesIds,
              date: toApiDate(expensesStore.modalForms.qrCodes.$.date.$),
              type: ExpensePaymentOrderExportType.QrCodesList,
            })
          }
          onClose={store.qrCodesModalConfirm.onClose}
        />

        <Modal
          {...store.qrCodesModal}
          onClose={store.qrCodesModal.onClose}
          trapFocus={false}
        >
          <ModalOverlay />
          <ModalContent minH="250px">
            <ModalHeader>
              <Trans>Generate QR codes</Trans>
            </ModalHeader>
            <ModalCloseButton />

            <ModalBody>
              <InputDatePicker
                mb="4"
                label={t`Date`}
                selected={expensesStore.modalForms.qrCodes.$.date.value}
                onChange={(range) => {
                  if (range) {
                    expensesStore.modalForms.qrCodes.$.date.onChange(
                      range.start,
                    );
                  }
                }}
              />
              <Select
                label={t`Card`}
                {...fieldToSelectProps(
                  expensesStore.modalForms.qrCodes.$.card_id,
                  expensesStore.cardOptions,
                )}
                asPortal
                placeholder={t`Choose card`}
              />
            </ModalBody>

            <ModalFooter>
              <ButtonGroup spacing="2">
                <CancelButton onClick={store.qrCodesModal.onClose} />
                <Button
                  isDisabled={expensesStore.modalForms.qrCodes.hasError}
                  isLoading={store.isPaymentOrderExportLoading}
                  onClick={store.validateQrCodesModal}
                >
                  <Trans>Generate</Trans>
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <Modal
          {...store.paymentOrderModal}
          onClose={store.paymentOrderModal.onClose}
          trapFocus={false}
        >
          <ModalOverlay />
          <ModalContent minH="250px">
            <ModalHeader>
              <Trans>Generate payment order</Trans>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <InputDatePicker
                mb="4"
                label={t`Date`}
                selected={expensesStore.modalForms.paymentOrder.$.date.value}
                onChange={(range) => {
                  if (range) {
                    expensesStore.modalForms.paymentOrder.$.date.onChange(
                      range.start,
                    );
                  }
                }}
              />
              <Select
                label={t`Agency bank account`}
                {...fieldToSelectProps(
                  expensesStore.modalForms.paymentOrder.$.bank_account_id,
                  expensesStore.bankAccountOptions,
                )}
                asPortal
                placeholder={t`Choose agency bank account`}
              />
            </ModalBody>
            <ModalFooter>
              <ButtonGroup spacing="2">
                <CancelButton onClick={store.paymentOrderModal.onClose} />
                <Button
                  isDisabled={expensesStore.modalForms.paymentOrder.hasError}
                  isLoading={store.isPaymentOrderExportLoading}
                  onClick={() =>
                    store.validateAndExportPaymentOrderModal({
                      payment_type_id:
                        expensesStore.modalForms.paymentOrder.$.bank_account_id
                          .$!,
                      ids: expensesStore.selectedExpensesIds,
                      date: toApiDate(
                        expensesStore.modalForms.paymentOrder.$.date.$,
                      ),
                      type: ExpensePaymentOrderExportType.PaymentOrder,
                    })
                  }
                >
                  <Trans>Generate</Trans>
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </ModalContent>
        </Modal>
        {can("expense_create") && (
          <ButtonLink
            href="/expenses/create"
            leftIcon={<Icon name="plus" />}
          >{t`New Expense`}</ButtonLink>
        )}
      </HStack>
    </HStack>
  );
}

const SelectExpensesFirstTooltip = ({
  children,
  disabled,
}: PropsWithChildren<{ disabled?: boolean }>) => {
  return (
    <Tooltip
      isDisabled={disabled}
      label={<Trans>Select expenses first</Trans>}
      placement="top"
    >
      {children}
    </Tooltip>
  );
};

export default observer(ExpenseListNavbar);
